/**
 * SNG Wizard Content for Poker Reborn
 *
 * Content section for the Sit & Go Custom Play wizard.
 * Provides a horizontal buy-in tier slider (12 discrete tiers) with locked 9-max seating.
 *
 * @see docs/screens/lobby/popups/WIZARD_SNG.md - Visual specs, dimensions, behavior
 * @see docs/screens/lobby/popups/CUSTOM_PLAY_WIZARD.md - Base wizard shell
 * @see src/utils/managerSelection.ts - Level data and manager lookup
 */

import { Container, Text, Graphics, FederatedPointerEvent } from 'pixi.js';
import type { ManagerConfig, SngTier } from '@/config/managers';
import { getAvailableLevels, findSngManager, formatChips } from '@/utils/managerSelection';
import type { LevelOption } from '@/utils/managerSelection';
import type { WizardContent } from './WizardContent';
import { WIZARD_CONTENT_STYLES } from './wizardStyles';

// =============================================================================
// SLIDER CONSTANTS
// =============================================================================

const SLIDER_TRACK_HEIGHT = 6;
const SLIDER_TRACK_RADIUS = 3;
const SLIDER_THUMB_RADIUS = 10;
const SLIDER_TICK_RADIUS = 2;

const SLIDER_TRACK_COLOR = 0x333344;
const SLIDER_FILL_COLOR = 0x2d8a27;
const SLIDER_THUMB_COLOR = 0xffffff;
const SLIDER_THUMB_HOVER_COLOR = 0xcccccc;
const SLIDER_THUMB_BORDER_COLOR = 0x000000;
const SLIDER_THUMB_BORDER_ALPHA = 0.3;
const SLIDER_THUMB_BORDER_WIDTH = 2;
const SLIDER_TICK_COLOR = 0x555566;

/** Height of the two-line info display above the slider */
const INFO_DISPLAY_HEIGHT = 34;
/** Gap between info display and slider track */
const INFO_TO_SLIDER_GAP = 12;
/** Gap between slider area and range labels */
const SLIDER_TO_RANGE_GAP = 4;
/** Height of range label text row */
const RANGE_LABELS_HEIGHT = 13;
/** Approximate height of a section label line */
const SECTION_LABEL_HEIGHT = 13;
/** Gap between range labels and info text */
const INFO_TEXT_GAP = 8;
/** Height of the info text line */
const INFO_TEXT_HEIGHT = 13;

// =============================================================================
// SNG WIZARD CONTENT
// =============================================================================

export class SngWizardContent implements WizardContent {
  readonly container: Container;

  private levels: readonly LevelOption[];
  private levelBuyins: readonly number[];
  private selectedLevel = 1;
  private readinessCallback: ((ready: boolean) => void) | null = null;
  private contentWidth: number;

  // Slider elements
  private sliderContainer: Container;
  private fill: Graphics;
  private thumb: Graphics;
  private tierText: Text;
  private buyinText: Text;

  // Slider interaction state
  private isDragging = false;
  private isHovering = false;

  constructor(contentWidth: number) {
    this.contentWidth = contentWidth;
    this.container = new Container();
    this.levels = getAvailableLevels('sng');

    // Precompute buy-in for each tier
    this.levelBuyins = this.levels.map((level) => {
      const manager = findSngManager(level.tier as SngTier);
      return manager?.buyin ?? 0;
    });

    let currentY = 0;

    // -- Section label: "Select Buy-In Tier:" --
    const tierLabel = new Text({
      text: 'Select Buy-In Tier:',
      style: WIZARD_CONTENT_STYLES.sectionLabel,
    });
    tierLabel.y = currentY;
    this.container.addChild(tierLabel);
    currentY += SECTION_LABEL_HEIGHT + WIZARD_CONTENT_STYLES.gaps.sectionLabelToButtons;

    // -- Tier info display (centered above slider) --
    this.tierText = new Text({
      text: '',
      style: {
        fill: 0xffffff,
        fontSize: 15,
        fontWeight: 'bold',
        fontFamily: 'Roboto, Arial, sans-serif',
      },
    });
    this.tierText.anchor.set(0.5, 0);
    this.tierText.x = contentWidth / 2;
    this.tierText.y = currentY;
    this.container.addChild(this.tierText);

    this.buyinText = new Text({
      text: '',
      style: {
        fill: 0x888899,
        fontSize: 12,
        fontFamily: 'Roboto, Arial, sans-serif',
      },
    });
    this.buyinText.anchor.set(0.5, 0);
    this.buyinText.x = contentWidth / 2;
    this.buyinText.y = currentY + 18;
    this.container.addChild(this.buyinText);
    currentY += INFO_DISPLAY_HEIGHT + INFO_TO_SLIDER_GAP;

    // -- Buy-in tier slider --
    this.sliderContainer = new Container();
    this.fill = new Graphics();
    this.thumb = new Graphics();
    this.sliderContainer.y = currentY;
    this.buildSlider();
    this.container.addChild(this.sliderContainer);
    currentY += SLIDER_THUMB_RADIUS * 2 + SLIDER_TO_RANGE_GAP;

    // -- Range labels --
    this.buildRangeLabels(currentY);
    currentY += RANGE_LABELS_HEIGHT + INFO_TEXT_GAP;

    // -- Info text: "9-max only" --
    const seatInfo = new Text({
      text: '9-max only',
      style: WIZARD_CONTENT_STYLES.infoText,
    });
    seatInfo.y = currentY;
    this.container.addChild(seatInfo);

    // Initial draw
    this.updateInfoDisplay();
    this.drawFill();
    this.drawThumb();
  }

  getContentHeight(): number {
    const { sectionLabelToButtons } = WIZARD_CONTENT_STYLES.gaps;
    return (
      SECTION_LABEL_HEIGHT + sectionLabelToButtons +
      INFO_DISPLAY_HEIGHT + INFO_TO_SLIDER_GAP +
      SLIDER_THUMB_RADIUS * 2 + SLIDER_TO_RANGE_GAP +
      RANGE_LABELS_HEIGHT + INFO_TEXT_GAP +
      INFO_TEXT_HEIGHT
    );
  }

  isReady(): boolean {
    return true; // Slider always has a valid selection
  }

  getSelectedManager(): ManagerConfig | undefined {
    const levelIndex = this.selectedLevel - 1;
    const level = this.levels[levelIndex];
    if (!level) return undefined;
    // SNG uses tier name for lookup, not level number
    return findSngManager(level.tier as SngTier);
  }

  onReadinessChange(callback: (ready: boolean) => void): void {
    this.readinessCallback = callback;
    // Defer so the caller finishes construction before the callback fires
    queueMicrotask(() => { callback(true); });
  }

  destroy(): void {
    this.container.destroy({ children: true });
  }

  // ===========================================================================
  // SLIDER BUILDING
  // ===========================================================================

  private buildSlider(): void {
    const trackCenterY = SLIDER_THUMB_RADIUS;

    // Track background
    const track = new Graphics();
    track.roundRect(
      0,
      trackCenterY - SLIDER_TRACK_HEIGHT / 2,
      this.contentWidth,
      SLIDER_TRACK_HEIGHT,
      SLIDER_TRACK_RADIUS,
    );
    track.fill(SLIDER_TRACK_COLOR);
    track.eventMode = 'none';
    this.sliderContainer.addChild(track);

    // Fill (green portion left of thumb, redrawn on selection change)
    this.fill.eventMode = 'none';
    this.sliderContainer.addChild(this.fill);

    // Tick marks (on top of fill so they're visible at all positions)
    const tickMarks = new Graphics();
    for (let i = 0; i < this.levels.length; i++) {
      tickMarks.circle(this.getStopX(i), trackCenterY, SLIDER_TICK_RADIUS);
    }
    tickMarks.fill(SLIDER_TICK_COLOR);
    tickMarks.eventMode = 'none';
    this.sliderContainer.addChild(tickMarks);

    // Thumb (redrawn on selection change)
    this.thumb.eventMode = 'none';
    this.sliderContainer.addChild(this.thumb);

    // Interaction — entire slider area is draggable/tappable
    this.sliderContainer.eventMode = 'static';
    this.sliderContainer.cursor = 'pointer';
    const hitPadding = 10;
    this.sliderContainer.hitArea = {
      contains: (x: number, y: number) => {
        return (
          x >= -hitPadding &&
          x <= this.contentWidth + hitPadding &&
          y >= trackCenterY - SLIDER_THUMB_RADIUS - hitPadding &&
          y <= trackCenterY + SLIDER_THUMB_RADIUS + hitPadding
        );
      },
    };

    this.sliderContainer.on('pointerdown', (event: FederatedPointerEvent) => {
      this.isDragging = true;
      this.updateSliderFromPointer(event);
    });
    this.sliderContainer.on('pointermove', (event: FederatedPointerEvent) => {
      if (!this.isDragging) return;
      this.updateSliderFromPointer(event);
    });
    this.sliderContainer.on('pointerup', () => {
      this.isDragging = false;
      this.drawThumb();
    });
    this.sliderContainer.on('pointerupoutside', () => {
      this.isDragging = false;
      this.drawThumb();
    });
    this.sliderContainer.on('pointerover', () => {
      this.isHovering = true;
      this.drawThumb();
    });
    this.sliderContainer.on('pointerout', () => {
      this.isHovering = false;
      this.drawThumb();
    });
  }

  // ===========================================================================
  // SLIDER DRAWING
  // ===========================================================================

  private getStopX(levelIndex: number): number {
    const stopCount = this.levels.length;
    if (stopCount <= 1) return 0;
    return (levelIndex / (stopCount - 1)) * this.contentWidth;
  }

  private drawFill(): void {
    const trackCenterY = SLIDER_THUMB_RADIUS;
    const levelIndex = this.selectedLevel - 1;
    const fillWidth = this.getStopX(levelIndex);

    this.fill.clear();
    if (fillWidth > 0) {
      this.fill.roundRect(
        0,
        trackCenterY - SLIDER_TRACK_HEIGHT / 2,
        fillWidth,
        SLIDER_TRACK_HEIGHT,
        SLIDER_TRACK_RADIUS,
      );
      this.fill.fill(SLIDER_FILL_COLOR);
    }
  }

  private drawThumb(): void {
    const trackCenterY = SLIDER_THUMB_RADIUS;
    const levelIndex = this.selectedLevel - 1;
    const thumbX = this.getStopX(levelIndex);
    const thumbColor = this.isHovering || this.isDragging
      ? SLIDER_THUMB_HOVER_COLOR
      : SLIDER_THUMB_COLOR;

    this.thumb.clear();
    this.thumb.circle(thumbX, trackCenterY, SLIDER_THUMB_RADIUS);
    this.thumb.fill(thumbColor);
    this.thumb.circle(thumbX, trackCenterY, SLIDER_THUMB_RADIUS);
    this.thumb.stroke({
      color: SLIDER_THUMB_BORDER_COLOR,
      width: SLIDER_THUMB_BORDER_WIDTH,
      alpha: SLIDER_THUMB_BORDER_ALPHA,
    });
  }

  private updateInfoDisplay(): void {
    const levelIndex = this.selectedLevel - 1;
    const level = this.levels[levelIndex];
    if (!level) return;

    this.tierText.text = level.tier;

    const buyin = this.levelBuyins[levelIndex];
    this.buyinText.text = buyin !== undefined
      ? `Buy-In: ${formatChips(buyin)}`
      : '';
  }

  private updateSliderFromPointer(event: FederatedPointerEvent): void {
    const local = this.sliderContainer.toLocal(event.global);
    const ratio = Math.max(0, Math.min(1, local.x / this.contentWidth));
    const levelIndex = Math.round(ratio * (this.levels.length - 1));
    const newLevel = levelIndex + 1;

    if (newLevel !== this.selectedLevel) {
      this.selectedLevel = newLevel;
      this.updateInfoDisplay();
      this.drawFill();
      this.drawThumb();
    }
  }

  // ===========================================================================
  // RANGE LABELS
  // ===========================================================================

  private buildRangeLabels(startY: number): void {
    const firstLevel = this.levels[0];
    const lastLevel = this.levels[this.levels.length - 1];
    const rangeLabelStyle = {
      fill: 0x666677,
      fontSize: 11,
      fontFamily: 'Roboto, Arial, sans-serif',
    };

    if (firstLevel) {
      const leftLabel = new Text({
        text: firstLevel.tier,
        style: rangeLabelStyle,
      });
      leftLabel.y = startY;
      this.container.addChild(leftLabel);
    }

    if (lastLevel) {
      const rightLabel = new Text({
        text: lastLevel.tier,
        style: rangeLabelStyle,
      });
      rightLabel.anchor.set(1, 0);
      rightLabel.x = this.contentWidth;
      rightLabel.y = startY;
      this.container.addChild(rightLabel);
    }
  }
}
